import React, { useState, useEffect } from 'react';
import { Animal } from '../types/Animal';
import './AnimalModal.css';

interface AnimalModalProps {
  animal: Animal;
  onClose: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: (animalId: string) => void;
  initialPhotoIndex?: number;
}

const AnimalModal: React.FC<AnimalModalProps> = ({ animal, onClose, isFavorite = false, onToggleFavorite, initialPhotoIndex = 0 }) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(initialPhotoIndex);
  
  console.log(`🔮 Modal opened for ${animal.name} with initialPhotoIndex: ${initialPhotoIndex}, currentPhotoIndex: ${currentPhotoIndex}`);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    console.log('Modal image failed to load:', e.currentTarget.src);
    
    // Try alternative URL formats if the first one fails
    const currentSrc = e.currentTarget.src;
    
    if (currentSrc.includes('export=download')) {
      // If download format failed, try the view format
      const fileId = currentSrc.match(/id=([^&]+)/)?.[1];
      if (fileId) {
        console.log('Trying uc?export=view format in modal...');
        e.currentTarget.src = `https://drive.google.com/uc?export=view&id=${fileId}`;
        return;
      }
    } else if (currentSrc.includes('export=view')) {
      // If view format failed, try thumbnail format
      const fileId = currentSrc.match(/id=([^&]+)/)?.[1];
      if (fileId) {
        console.log('Trying thumbnail format in modal...');
        e.currentTarget.src = `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000-h1000`;
        return;
      }
    }
    
    // If all formats fail, use placeholder
    e.currentTarget.src = '/placeholder-animal.svg';
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev === animal.photos.length - 1 ? 0 : prev + 1
    );
  };

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => 
      prev === 0 ? animal.photos.length - 1 : prev - 1
    );
  };

  const handleImageClick = (e: React.MouseEvent) => {
    if (animal.photos.length <= 1) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const centerX = rect.width / 2;
    
    if (clickX > centerX) {
      // Right half - next photo
      nextPhoto();
    } else {
      // Left half - previous photo
      prevPhoto();
    }
  };


  const handleInterestClick = () => {
    const subject = `Interest in ${animal.name}`;
    const body = `Hi, I'm interested in learning more about ${animal.name}, a ${animal.age} ${animal.breed}. Please contact me with more information.`;
    window.location.href = `mailto:shelter@example.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content">

        <div className="modal-body">
          <div className="modal-photos">
            {animal.photos.length > 0 ? (
              <>
                <div className="photo-main">
                  <button className="modal-close-x" onClick={onClose} title="Close">
                    ×
                  </button>
                  <img
                    src={animal.photos[currentPhotoIndex]}
                    alt={`${animal.name} ${currentPhotoIndex + 1}`}
                    onError={handleImageError}
                    onClick={handleImageClick}
                    style={{ cursor: animal.photos.length > 1 ? 'pointer' : 'default' }}
                  />
                  
                  {animal.photos.length > 1 && (
                    <>
                      <button className="photo-nav photo-nav--prev" onClick={prevPhoto}>
                        ‹
                      </button>
                      <button className="photo-nav photo-nav--next" onClick={nextPhoto}>
                        ›
                      </button>
                    </>
                  )}
                </div>

                {animal.photos.length > 1 && (
                  <div className="photo-dots-modal">
                    {animal.photos.map((_, index) => (
                      <button
                        key={index}
                        className={`photo-dot-modal ${index === currentPhotoIndex ? 'active' : ''}`}
                        onClick={() => setCurrentPhotoIndex(index)}
                        title={`Photo ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="photo-placeholder">
                <span>No photos available</span>
              </div>
            )}
          </div>

          <div className="modal-info">
            <div className="animal-header">
              <h1 className="animal-name">{animal.name}</h1>
              <div className="animal-basic-info">
                <span className="info-item">{animal.breed}</span>
                <span className="info-separator">•</span>
                <span className="info-item">{animal.color}</span>
                <span className="info-separator">•</span>
                <span className="info-item">{animal.sex}</span>
                <span className="info-separator">•</span>
                <span className="info-item">{animal.age}</span>
              </div>
              {animal.tags && (
                <div className="animal-features">
                  {animal.tags}
                </div>
              )}
            </div>

            {animal.story && (
              <div className="animal-story">
                <h3>About Me</h3>
                <p>{animal.story}</p>
              </div>
            )}

            <div className="modal-actions">
              <button className="modal-action-btn" onClick={handleInterestClick} title="Express Interest">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </button>
              {onToggleFavorite && (
                <button 
                  className={`modal-action-btn ${isFavorite ? 'favorited' : ''}`}
                  onClick={() => onToggleFavorite(animal.id)}
                  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnimalModal;